"use client";

import Script from "next/script";
import { useEffect, useContext, createContext, useState } from "react";
import { getApiUrl } from "@repo/utils/get-api-url";
import { handleOneTrustLoad } from "@/components/consent";
import { ADOBE_ANALYTICS_SCRIPT_URL } from "@/constants";

type AnalyticsContext = {
  isReady: boolean;
  pushToDataLayer: (data: object) => void;
  resetDataLayer: () => void;
};

const defaultAnalyticsContextValue = {
  isReady: false,
  pushToDataLayer: () => {},
  resetDataLayer: () => {},
} as AnalyticsContext;
export const AnalyticsContext = createContext(defaultAnalyticsContextValue);
export const useAnalytics = () => useContext(AnalyticsContext);

const logger = (error: Error) => {
  if (console) {
    console.error(error);
  }
};

const createWaitingSink = () => {
  let resolveWaitingSink: () => void = () => {};
  const waitingSink = new Promise<void>((resolve) => {
    resolveWaitingSink = resolve;
  });

  return { waitingSink, resolveWaitingSink };
};

// create fake source for now just to kick off the bridging (will refactor when tracking bridge is updated)
const createFakeSource = () => {
  const { port1: publicEventTarget } = new MessageChannel();
  return Promise.resolve(publicEventTarget);
};

export const AnalyticsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [analytics, setAnalytics] = useState(defaultAnalyticsContextValue);

  const fakeSource = createFakeSource();
  const { waitingSink, resolveWaitingSink } = createWaitingSink();

  useEffect(() => {
    if (analytics.isReady) {
      return;
    }

    import("@asosteam/asos-web-event-tracking-bridge").then(
      ({ initialise, createGtmSink, createMixpanelSink }) => {
        const gtmSink = createGtmSink(
          "GTM-WSDVJP8M",
          logger,
          true,
          undefined,
          "GTM-PQSSZK68",
        );

        const MIXPANEL_PROJECT_TOKEN = "TSTM";
        const targetAnalyticsApiEndpoint = getApiUrl({
          path: "/customer/signals/v1",
          searchParams: {},
        });
        const mixpanelSink = createMixpanelSink(MIXPANEL_PROJECT_TOKEN, {
          api_host: targetAnalyticsApiEndpoint,
        });

        type ChameleonSourcePromises = {
          [key: string]: Promise<EventTarget>;
        };
        type ChameleonSinkPromises = {
          adobe: Promise<void>;
          gtm: typeof gtmSink;
          mixpanel: typeof mixpanelSink;
        };

        const initialisedAnalytics = initialise({
          sources: {
            fakeSource,
          } as ChameleonSourcePromises,
          sinks: {
            gtm: gtmSink,
            adobe: waitingSink,
            mixpanel: mixpanelSink,
          } as ChameleonSinkPromises,
          logError: logger,
        });

        handleOneTrustLoad(initialisedAnalytics.pushToDataLayer);

        setAnalytics({
          isReady: true,
          pushToDataLayer: initialisedAnalytics.pushToDataLayer,
          resetDataLayer: initialisedAnalytics.resetDataLayer,
        });
      },
    );
  }, [analytics.isReady, fakeSource, waitingSink]);

  return (
    <>
      <AnalyticsContext.Provider value={{ ...analytics }}>
        {children}
      </AnalyticsContext.Provider>
      <Script
        id="adobe-web-sdk"
        src={ADOBE_ANALYTICS_SCRIPT_URL}
        type="text/javascript"
        async
        onLoad={resolveWaitingSink}
      ></Script>
    </>
  );
};
